import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { CustomerId, FacilityId } from "../lib/workIds";

// IWA-18987 narrowed to detecting newly created Requests on the active list
// only — badge/count freshness is out of scope, so this keeps its own
// minimal interval.
const UNREAD_COUNT_REFETCH_INTERVAL_MS = 60_000;

export const unreadWorkRequestCountQueryKey = "work-requests-unread-count";

const unreadWorkRequestCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});

async function fetchUnreadWorkRequestCount(
  customerId: CustomerId,
  facilityId: FacilityId,
): Promise<number> {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ].requests["unread-count"].$get({
    param: { customerId, facilityId },
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch the unread Work Request count: ${response.status}`,
    );
  }
  const payload = unreadWorkRequestCountResponseSchema.parse(
    await response.json(),
  );
  return payload.count;
}

export function unreadWorkRequestCountQueryOptions(
  customerId: CustomerId | undefined,
  facilityId: FacilityId | undefined,
) {
  return queryOptions({
    queryKey: [unreadWorkRequestCountQueryKey, customerId, facilityId],
    queryFn: () => {
      if (!customerId || !facilityId) {
        throw new Error("A customer and facility must be selected.");
      }
      return fetchUnreadWorkRequestCount(customerId, facilityId);
    },
    refetchInterval: UNREAD_COUNT_REFETCH_INTERVAL_MS,
  });
}

export function useUnreadWorkRequestCountQuery(options?: {
  enabled?: boolean;
}) {
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useQuery({
    ...unreadWorkRequestCountQueryOptions(
      selectedCustomer?.id,
      selectedFacility?.id,
    ),
    enabled:
      Boolean(selectedCustomer && selectedFacility) &&
      (options?.enabled ?? true),
  });
}
